import './Segmentation.css';

// Libraries
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ucWords } from 'utils/text.util';
import { useNavigate, useLocation } from 'react-router-dom';

// Components
import Search from 'components/Search/Search';
import DataTable from 'components/DataTable/DataTable';
import Modal from 'components/Modal/Modal';
import Loader from 'components/Loader/Loader';
import IconButton from 'components/Buttons/IconButton/IconButton';
import { CurrentPermissionContext } from 'components/AccessWrapper/AccessWrapper';
import DynamicAppFlowsButton from 'components/DynamicAppFlowsButton/DynamicAppFlowsButton';

// Utils
import { formatToUTCDate, isValidDate } from 'utils/date.util';
import { useFetch } from 'utils/rest/request';
import { canCreate, canDelete } from 'utils/permission.util';
import {
    GET_SEGMENTS,
    DELETE_SEGMENT,
    GET_SEGMENTATION_COUNT,
    GET_ARE_SEGMENT_RESULTS_RUNNING,
    useMutation,
    useLazyQuery,
    useQuery,
} from 'utils/graphql';

// Assets
import { ReactComponent as AddIcon } from 'assets/icons/add_icon.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete_icon.svg';
import { ReactComponent as PlayIcon } from 'assets/icons/play_icon_24.svg';
import { ReactComponent as DownloadIcon } from 'assets/icons/download_icon_24.svg';
import { ReactComponent as ErrorIcon } from 'assets/icons/exclamation-triangle.svg';

function parseUrlParams(search) {
    const params = new URLSearchParams(search);
    return {
        filter: params.get('query') || '',
        page: parseInt(params.get('page') || '1', 10),
        perPage: parseInt(params.get('perPage') || '15', 10),
    };
}

function Segmentation() {
    const { permissionSet } = useContext(CurrentPermissionContext);
    const navigate = useNavigate();
    const location = useLocation();

    // Initialise from URL params once — prevents mount-time double-fetch
    const [filter, setFilter] = useState(() => parseUrlParams(location.search).filter);
    const [page, setPage] = useState(() => parseUrlParams(location.search).page);
    const [perPage, setPerPage] = useState(() => parseUrlParams(location.search).perPage);

    const [modalIsOpen, setIsOpen] = useState(false);
    const [modalType, setModalType] = useState('delete');
    const [segmentToDelete, setSegmentToDelete] = useState();
    const [loading, setLoading] = useState(true);
    const [tableData, setTableData] = useState();

    const { data: count, refetch: refreshCount } = useQuery(GET_SEGMENTATION_COUNT, {
        variables: { isConsent: false, name: filter },
    });

    const [getSegments, { data: segmentationData }] = useLazyQuery(GET_SEGMENTS, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            refreshCount();
            setTableData(data);
            setLoading(false);
        },
    });

    const [getAreResultsRunning] = useLazyQuery(GET_ARE_SEGMENT_RESULTS_RUNNING, {
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'no-cache',
    });

    const {
        data: appsData,
        error: appsError,
        loading: appsLoading,
    } = useFetch('/api/connected_apps', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        timeout: 3000,
    });

    const { data: flowsData, loading: flowsLoading } = useFetch(
        '/api/connected_apps/tenant/routines',
        {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            timeout: 3000,
        }
    );

    const [deleteSegment] = useMutation(DELETE_SEGMENT);

    // --- Handlers ---

    const openModal = useCallback((type) => {
        setModalType(type);
        setIsOpen(true);
    }, []);

    const handlePageChange = useCallback((newPage) => setPage(newPage), []);
    const handlePerRowsChange = useCallback((newPerPage) => setPerPage(newPerPage), []);
    const handleNewClick = useCallback(() => navigate('/segmentation/new'), [navigate]);
    const handleNewFlow = useCallback((id) => navigate(`/segmentation/flow/new/${id}`), [navigate]);
    const handleDownload = useCallback(
        (id) => navigate(`/segmentation/download/${id}`),
        [navigate]
    );
    const handleSegmentSelect = useCallback(
        (row) => navigate(`/segmentation/${row.id}`),
        [navigate]
    );

    const handleDelete = useCallback(
        async (segment) => {
            const { data, error } = await getAreResultsRunning({
                variables: { segmentationId: segment.id },
            });
            if (error) {
                openModal('cannotDeleteError');
            } else if (data.segmentationAreResultsRunning.active) {
                openModal('resultsRunningDeleteError');
            } else {
                setSegmentToDelete(segment);
                openModal('delete');
            }
        },
        [getAreResultsRunning, openModal]
    );

    const handleSearch = useCallback(() => {
        setLoading(true);
        window.history.replaceState(
            null,
            '',
            `?query=${encodeURIComponent(filter)}&page=${page}&perPage=${perPage}`
        );
        getSegments({
            variables: {
                search: filter,
                offset: perPage * (page - 1),
                limit: perPage,
                isConsent: false,
            },
        });
    }, [getSegments, filter, page, perPage]);

    // --- Columns (derived via useMemo — no state needed, auto-updates with deps) ---

    const columns = useMemo(
        () => [
            {
                name: 'Name',
                sortable: false,
                selector: (row) => row.name,
                format: (row) => ucWords(row.name),
            },
            {
                name: 'Consent',
                sortable: false,
                selector: (row) => row?.consent?.name,
            },
            {
                name: 'Modified',
                sortable: false,
                selector: (row) => {
                    const last_audit_date = new Date(
                        Math.max(
                            ...row?.audits.edges.map((audit) => new Date(audit.node.createdAt))
                        )
                    );
                    const modified_date = new Date(last_audit_date?.toString() || row.created_at);
                    if (!isValidDate(modified_date)) return '';
                    return formatToUTCDate(modified_date);
                },
            },
            {
                name: 'Flows',
                sortable: false,
                allowOverflow: true,
                selector: (row) => {
                    if (!row?.flows?.length) return null;
                    return (
                        <div className="flow-btns">
                            {row.flows.map((flow, i) => (
                                <DynamicAppFlowsButton
                                    key={i}
                                    flows={flow.routines}
                                    app={flow.app}
                                    connection={flow.connection}
                                    segmentId={row.id}
                                />
                            ))}
                        </div>
                    );
                },
            },
            {
                name: canCreate(permissionSet) && (
                    <IconButton onClick={handleNewClick} padding="8px">
                        <AddIcon fill="var(--accent-color)" height="24" width="24" />
                    </IconButton>
                ),
                sortable: false,
                width: '152px',
                right: true,
                selector: (row) => (
                    <div className="action-btns">
                        <IconButton
                            onClick={() => {
                                if (appsLoading) return openModal('flowsLoading');
                                if (appsError) return openModal('flowsLoadingError');
                                handleNewFlow(row.id);
                            }}
                            padding="8px"
                            tooltip="Create Flow"
                        >
                            <PlayIcon
                                fill={
                                    appsLoading || appsError
                                        ? 'var(--greyscale-highlight-color)'
                                        : 'var(--font-color)'
                                }
                                height="24"
                                width="24"
                            />
                        </IconButton>
                        <IconButton
                            onClick={() => handleDownload(row.id)}
                            padding="8px"
                            tooltip="Export CSV"
                        >
                            <DownloadIcon fill="var(--font-color)" height="24" width="24" />
                        </IconButton>
                        {canDelete(permissionSet) && (
                            <IconButton
                                onClick={() => handleDelete(row)}
                                padding="8px"
                                tooltip="Delete"
                            >
                                <DeleteIcon fill="var(--error-color)" height="24" width="24" />
                            </IconButton>
                        )}
                    </div>
                ),
            },
        ],
        [
            permissionSet,
            appsLoading,
            appsError,
            handleNewClick,
            handleNewFlow,
            handleDownload,
            handleDelete,
            openModal,
        ]
    );

    // --- Combine segment list with flows ---

    const combineSegmentFlows = useCallback((segmentList, appList = [], flowsList = []) => {
        const updatedSegments = segmentList.segmentationList.edges.map(({ node }) => {
            const routines = flowsList.filter((flow) => flow.source_id === node.id);
            if (routines.length === 0) return { node: { ...node, flows: [] } };

            const flows = appList.flatMap((app) =>
                app.connections.reduce((acc, connection) => {
                    const appRoutines = routines.filter(
                        (routine) => routine.connection.id === connection.id
                    );
                    if (appRoutines.length > 0)
                        acc.push({ app, connection, routines: appRoutines });
                    return acc;
                }, [])
            );

            return { node: { ...node, flows } };
        });
        setTableData({ segmentationList: { edges: updatedSegments } });
    }, []);

    // --- Modal options ---

    const getModalOptions = useCallback(
        (type) => {
            switch (type) {
                case 'delete':
                    return {
                        type: 'confirmation',
                        title: 'Warning',
                        content: (
                            <div>
                                <p>Are you sure you want to delete this segment?</p>
                                <b>{segmentToDelete?.name}</b>
                            </div>
                        ),
                        confirmBtnText: 'Yes',
                        cancelBtnText: 'No',
                        width: '250px',
                        textAlign: 'center',
                        confirmationBtnAction: async () => {
                            deleteSegment({
                                variables: { id: segmentToDelete.id },
                                onCompleted: (data) => {
                                    if (data.deleteSegmentation) {
                                        getSegments({
                                            variables: {
                                                search: filter,
                                                offset: perPage * (page - 1),
                                                limit: perPage,
                                                isConsent: false,
                                            },
                                        });
                                        openModal('deleted');
                                    } else {
                                        openModal('deleteError');
                                    }
                                },
                            });
                        },
                    };
                case 'deleted':
                    return {
                        title: 'Success',
                        content: <p>Segment has been deleted.</p>,
                        width: '250px',
                        textAlign: 'center',
                        confirmationBtnAction: () => navigate('/segmentation'),
                    };
                case 'deleteError':
                    return {
                        title: <ErrorIcon fill="var(--error-color)" width="40" height="40" />,
                        content: <p>Segment could not be deleted.</p>,
                        width: '250px',
                        textAlign: 'center',
                    };
                case 'cannotDeleteError':
                    return {
                        title: <ErrorIcon fill="var(--error-color)" width="40" height="40" />,
                        content: <p>Segment cannot be deleted. Try again later.</p>,
                        width: '250px',
                        textAlign: 'center',
                    };
                case 'resultsRunningDeleteError':
                    return {
                        title: <ErrorIcon fill="var(--error-color)" width="40" height="40" />,
                        content: (
                            <p>
                                You cannot delete the segmentation while the results are being
                                processed. Try again later.
                            </p>
                        ),
                        width: '350px',
                        textAlign: 'center',
                    };
                case 'flowsLoading':
                    return {
                        title: 'Loading!',
                        content: <p>Hold on a sec while we load.</p>,
                        width: '250px',
                        textAlign: 'center',
                    };
                case 'flowsLoadingError':
                    return {
                        title: <ErrorIcon fill="var(--error-color)" width="40" height="40" />,
                        content: (
                            <div>
                                <p>Creating flows is currently unavailable.</p>
                                <p>Please try again later.</p>
                            </div>
                        ),
                        width: '250px',
                        textAlign: 'center',
                    };
                default:
                    return {};
            }
        },
        [segmentToDelete, deleteSegment, getSegments, filter, page, perPage, openModal, navigate]
    );

    // --- Effects ---

    /**
     * Combine segment list with apps & flows once all fetches settle.
     * Falls back to empty arrays if apps/flows failed (e.g. 401).
     */
    useEffect(() => {
        if (!segmentationData || loading || appsLoading || flowsLoading) return;
        combineSegmentFlows(segmentationData, appsData?.rows ?? [], flowsData?.rows ?? []);
    }, [
        segmentationData,
        appsData,
        flowsData,
        loading,
        appsLoading,
        flowsLoading,
        combineSegmentFlows,
    ]);

    /**
     * Run search whenever filter, page, or perPage changes (including on mount).
     * URL params are read into state once via useState initialisers — no extra effects needed.
     */
    useEffect(() => {
        handleSearch();
    }, [filter, page, perPage]);

    return (
        <div className="segmentation-list">
            <div className="segmentation-list-header">
                <Search handler={setFilter} />
            </div>
            <DataTable
                columns={columns}
                data={tableData?.segmentationList.edges.map(({ node }) => node)}
                noDataComponent="You don't have any segments."
                pagination={true}
                paginationServer={true}
                paginationTotalRows={count?.segmentationListCount || 0}
                paginationPerPage={perPage}
                paginationDefaultPage={page}
                progressPending={loading}
                progressComponent={<Loader />}
                onChangeRowsPerPage={handlePerRowsChange}
                onChangePage={handlePageChange}
                defaultSortFieldId={1}
                highlightOnHover={true}
                onRowClicked={handleSegmentSelect}
                persistTableHead={true}
                selectableRows={false}
            />
            <Modal options={getModalOptions(modalType)} isOpen={modalIsOpen} setOpen={setIsOpen} />
        </div>
    );
}

export default Segmentation;
